import ImageJs from "image-js";

function drawGradientRectangle(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  gradientColors: string[],
  borderRadius = 0
) {
  // Crear gradiente vertical
  const gradient = ctx.createLinearGradient(x, y, x, y + height);
  // gradientColors es un array con los colores; p.ej: ["rgba(0,0,0,0.70)", ...]
  // Para escalonar de forma uniforme, calculamos cada addColorStop
  gradientColors.forEach((color, index) => {
    const stop = index / (gradientColors.length - 1);
    gradient.addColorStop(stop, color);
  });

  // Dibujar el rectángulo con esquinas redondeadas
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.moveTo(x + borderRadius, y);
  ctx.lineTo(x + width - borderRadius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + borderRadius);
  ctx.lineTo(x + width, y + height - borderRadius);
  ctx.quadraticCurveTo(
    x + width,
    y + height,
    x + width - borderRadius,
    y + height
  );
  ctx.lineTo(x + borderRadius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - borderRadius);
  ctx.lineTo(x, y + borderRadius);
  ctx.quadraticCurveTo(x, y, x + borderRadius, y);
  ctx.closePath();
  ctx.fill();
}

/**
 * Función para hacer "wrap" de texto en un canvas.
 */
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number
): number {
  const words = text.split(" ");
  let line = "";
  let lineCount = 0;

  for (let i = 0; i < words.length; i++) {
    const testLine = line + words[i] + " ";
    const metrics = ctx.measureText(testLine);
    const testWidth = metrics.width;

    if (testWidth > maxWidth && i > 0) {
      // Dibuja la línea anterior
      ctx.fillText(line, x, y + lineCount * lineHeight);
      line = words[i] + " ";
      lineCount++;
    } else {
      line = testLine;
    }
  }
  // Dibuja la última línea
  ctx.fillText(line, x, y + lineCount * lineHeight);

  // Retorna la posición vertical final
  return y + (lineCount + 1) * lineHeight;
}

/**
 * Agrega un watermark con texto (Productor, Finca y fecha/hora) sobre la imagen.
 * @param photo Base64 o Blob de la foto (parámetro que entienda ImageJs.load)
 * @param producer Nombre del productor
 * @param farm Nombre de la finca
 * @returns String base64 (image/webp) con el watermark.
 */
export async function handleWatermark(
  photo: any,
  producer: string,
  farm: string
): Promise<string> {
  try {
    // 1. Cargar la imagen con ImageJs
    const image = await ImageJs.load(photo);
    const canvas = image.getCanvas();
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("No se pudo obtener el contexto 2D del canvas.");
    }

    // 2. Dibujar un rectángulo degradado en la parte inferior
    const rectHeight = 40;
    const rectY = image.height - rectHeight;
    drawGradientRectangle(ctx, 0, rectY, image.width, rectHeight, [
      "rgba(0, 0, 0, 0.70)",
      "rgba(3, 34, 99, 0.70)",
      "rgba(3, 34, 99, 0.70)",
      "rgba(160, 191, 242, 0.70)",
    ]);

    // 3. Preparar el texto a dibujar
    const now = new Date();
    const timeString = now.toLocaleTimeString(undefined, {
      hour12: false,
      timeStyle: "short",
    });
    const dateString = now.toLocaleDateString();

    // 4. Configurar fuente y color del texto
    ctx.fillStyle = "yellowgreen";
    ctx.font = "bold 22px Arial";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";

    // 5. Escribir el texto con wrapping
    //    Ej: "Productor: XXX, Finca: XXX, 23/01/2025 14:33"
    const textLine = `Productor: ${producer}, Finca: ${farm}, ${dateString} ${timeString}`;
    wrapText(ctx, textLine, 10, rectY + 6, image.width - 20, 20);

    // 6. Convertir canvas a base64
    const base64Image = canvas.toDataURL("image/webp");
    return base64Image;
  } catch (err) {
    console.error("Error al procesar watermark:", err);
    throw err; // Propagamos el error o puedes retornar un fallback
  }
}

export const handleWatermarkCheckPoint = (photo: any) =>
  new Promise(async (resolve, reject) => {
    const image = await ImageJs.load(photo);
    const canvas = image.getCanvas();
    const ctx = canvas.getContext("2d");
    // Define the rectangle properties
    const rectWidth = image.width;
    const rectHeight = 40;
    const rectX = 0; // Adjust position as needed
    const rectY = image.height - rectHeight; // Adjust position as needed
    const borderRadius = 0; // Border radius for rounded corners

    // Draw the gradient rectangle with rounded corners
    const gradient = ctx!.createLinearGradient(
      rectX,
      rectY,
      rectX,
      rectY + rectHeight
    );
    gradient.addColorStop(0, "rgba(0, 0, 0, 0.70)"); // Start with black and 70% opacity
    gradient.addColorStop(0.5, "rgba(3, 34, 99, 0.70)"); // Transition to #032263 with 70% opacity
    gradient.addColorStop(0.8, "rgba(3, 34, 99, 0.70)"); // Transition to #032263 with 70% opacity
    gradient.addColorStop(1, "rgba(160, 191, 242, 0.70)"); // End with a light blue with 70% opacity
    ctx!.fillStyle = gradient;
    ctx!.beginPath();
    ctx!.moveTo(rectX + borderRadius, rectY);
    ctx!.lineTo(rectX + rectWidth - borderRadius, rectY);
    ctx!.quadraticCurveTo(
      rectX + rectWidth,
      rectY,
      rectX + rectWidth,
      rectY + borderRadius
    );
    ctx!.lineTo(rectX + rectWidth, rectY + rectHeight - borderRadius);
    ctx!.quadraticCurveTo(
      rectX + rectWidth,
      rectY + rectHeight,
      rectX + rectWidth - borderRadius,
      rectY + rectHeight
    );
    ctx!.lineTo(rectX + borderRadius, rectY + rectHeight);
    ctx!.quadraticCurveTo(
      rectX,
      rectY + rectHeight,
      rectX,
      rectY + rectHeight - borderRadius
    );
    ctx!.lineTo(rectX, rectY + borderRadius);
    ctx!.quadraticCurveTo(rectX, rectY, rectX + borderRadius, rectY);
    ctx!.closePath();
    ctx!.fill();

    // Define the text properties
    const date = new Date();
    const timeString = date.toLocaleTimeString(undefined, {
      hour12: false,
      timeStyle: "short",
    });
    const dateString = date.toLocaleDateString();
    const dayString = date.toLocaleDateString(undefined, { weekday: "long" });

    // Custom info
    const clientName = "client";
    const producerName = "producer";
    const farmName = "farm";

    ctx!.fillStyle = "yellowgreen";
    ctx!.font = "25px Arial";
    // ctx!.fontStretch= "semi-condensed"
    ctx!.textAlign = "left";
    ctx!.textBaseline = "top";

    const wrapText = (
      text: string,
      x: number,
      y: number,
      maxWidth: number,
      lineHeight: number
    ) => {
      const words = text.split(" ");
      let line = "";
      let lineCount = 0;

      for (let i = 0; i < words.length; i++) {
        const testLine = line + words[i] + " ";
        const metrics = ctx!.measureText(testLine);
        const testWidth = metrics.width;

        if (testWidth > maxWidth && i > 0) {
          ctx!.fillText(line, x, y + lineCount * lineHeight);
          line = words[i] + " ";
          lineCount++;
        } else {
          line = testLine;
        }
      }
      ctx!.fillText(line, x, y + lineCount * lineHeight);
      return y + (lineCount + 1) * lineHeight; // Return the next starting Y position
    };

    // Add the text with wrapping
    ctx!.font = "bold 22px Arial";
    let startY = rectY + 6;
    startY = wrapText(
      `${dateString} ${timeString}`,
      rectX + 10,
      startY,
      rectWidth - 5,
      20
    );

    const base64Image = canvas.toDataURL("image/webp");
    resolve(base64Image);
  });
